// src/features/affiliate/admin/PayoutsTab.tsx
// Admin: approve, reject or process a partner's payout request.

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { useAffiliatePayout, useI18n } from '@react/hooks';
import { Button, Input, Alert } from '@react/components';
import { Select } from '@/components/Select';
import type { Payout, PayoutStatus } from '@core/types/affiliate';
import { PayoutStatusBadge } from '../badges';
import { errMessage, fmtDate, formatMoney, listParams } from '../format';

const STATUS_FILTERS: (PayoutStatus | '')[] = ['', 'PAYOUT_STATUS_PENDING', 'PAYOUT_STATUS_APPROVED', 'PAYOUT_STATUS_PROCESSING', 'PAYOUT_STATUS_COMPLETED', 'PAYOUT_STATUS_REJECTED', 'PAYOUT_STATUS_FAILED'];

export const PayoutsTab = observer(() => {
  const { t, locale } = useI18n();
  const store = useAffiliatePayout();

  const [status, setStatus] = useState<PayoutStatus | ''>('');
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rejectTarget, setRejectTarget] = useState<Payout | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const load = () => {
    setError(null);
    store.ListPayouts(listParams({ limit: 100 }, { status: status || undefined })).catch((e: unknown) => setError(errMessage(e, t('affiliate.admin.payouts.loadError'))));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const run = async (id: string, fn: () => Promise<unknown>) => {
    setBusyId(id);
    setError(null);
    try {
      await fn();
      load();
    } catch (e) {
      setError(errMessage(e, t('affiliate.admin.payouts.actionError')));
    } finally {
      setBusyId(null);
    }
  };

  const reject = async () => {
    if (!rejectTarget?.entity) return;
    await run(rejectTarget.entity.id, () => store.RejectPayout(rejectTarget.entity!.id, { entity: rejectTarget.entity!, reason: rejectReason }));
    setRejectTarget(null);
    setRejectReason('');
  };

  return (
    <div className="space-y-3">
      <Select
        value={status}
        onChange={(e) => setStatus(e.target.value as PayoutStatus | '')}
        className="w-52"
        options={STATUS_FILTERS.map((s) => ({ value: s, label: s ? t(`affiliate.payoutStatus.${s.replace('PAYOUT_STATUS_', '').toLowerCase()}`) : t('common.all') }))}
      />

      {error && <Alert variant="error">{error}</Alert>}
      {store.isLoading && store.items.length === 0 && <div className="h-24 animate-pulse rounded-md bg-muted" aria-busy="true" />}
      {!store.isLoading && store.items.length === 0 && !error && (
        <div className="rounded-md border border-dashed border-border p-8 text-center">
          <p className="text-sm text-muted-foreground">{t('affiliate.payouts.empty')}</p>
        </div>
      )}

      <div className="space-y-2">
        {store.items.map((p) => (
          <div key={p.entity?.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border p-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium text-foreground">{formatMoney(p.amount, locale)}</span>
                <PayoutStatusBadge status={p.status} />
              </div>
              <p className="text-sm text-muted-foreground">{fmtDate(p.created_at, locale)} · {p.conversions_count} {t('affiliate.admin.conversions.title')?.toLowerCase()}</p>
            </div>
            <div className="flex shrink-0 gap-1.5">
              {p.status === 'PAYOUT_STATUS_PENDING' && p.entity && (
                <>
                  <Button size="sm" onClick={() => run(p.entity!.id, () => store.ApprovePayout(p.entity!.id, { entity: p.entity! }))} isLoading={busyId === p.entity.id}>
                    {t('affiliate.admin.payouts.approve')}
                  </Button>
                  <Button size="sm" variant="danger" onClick={() => setRejectTarget(p)}>{t('affiliate.admin.payouts.reject')}</Button>
                </>
              )}
              {p.status === 'PAYOUT_STATUS_APPROVED' && p.entity && (
                <Button size="sm" onClick={() => run(p.entity!.id, () => store.ProcessPayout(p.entity!.id, { entity: p.entity! }))} isLoading={busyId === p.entity.id}>
                  {t('affiliate.admin.payouts.process')}
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {rejectTarget && (
        <div className="rounded-md border border-border p-3">
          <p className="text-sm font-medium text-foreground">{t('affiliate.admin.payouts.rejectTitle')}</p>
          <Input className="mt-2" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder={t('affiliate.admin.conversions.rejectReasonPlaceholder')} />
          <div className="mt-2 flex gap-2">
            <Button size="sm" variant="danger" onClick={reject} isLoading={busyId === rejectTarget.entity?.id}>{t('affiliate.admin.payouts.reject')}</Button>
            <Button size="sm" variant="ghost" onClick={() => setRejectTarget(null)}>{t('common.cancel')}</Button>
          </div>
        </div>
      )}
    </div>
  );
});
